<template>
  <div class="page-class page-setting-form">
    <h1 class="page-title text-center">{{ title }}</h1>

    <div>
      <div v-if="loading">
        <span class="spinner"></span>
      </div>
      <div v-if="!loading">
        <b-row>
          <b-col cols="12">
            <b-card :class="{ 'bg-dark text-light': isDarkMode }">
              <b-form @submit.prevent="onSubmit">
                <b-row>
                  <b-col md="6">
                    <b-form-group label="Subject" label-for="subject">
                      <b-form-input
                        id="subject"
                        v-model="form.subject"
                        type="text"
                        required
                        placeholder="Enter Here"
                        :class="{
                          'bg-light text-dark': !isDarkMode,
                          'bg-dark text-light placeholder-light': isDarkMode
                        }"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="Select Store" label-for="storeid">
                      <b-form-select
                        id="storeid"
                        v-model="form.storeid"
                        required
                        @change="onStoreChange"
                        :class="{
                          'bg-light text-dark': !isDarkMode,
                          'bg-dark text-light': isDarkMode
                        }"
                      >
                        <option value="">Select Store</option>
                        <option v-for="store in storeList" :key="store._id" :value="store.storeid">
                          {{ store.storeName }} ({{ store.storeid }})
                        </option>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="6">
                    <b-form-group label="Tags" label-for="role">
                      <b-form-select
                        id="role"
                        v-model="form.role"
                        required
                        @change="onRoleChange"
                        :class="{
                          'bg-light text-dark': !isDarkMode,
                          'bg-dark text-light': isDarkMode
                        }"
                      >
                        <option value="">Select Tag</option>
                        <option value="HO">HO</option>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                  <b-col md="6" v-if="form.role === 'HO'">
                    <b-form-group label="E-mail">
                      <div class="email-input-container mb-3">
                        <b-form-input
                          v-model="newEmail"
                          placeholder="Enter here"
                          class="email-input"
                          :class="{
                            'bg-light text-dark': !isDarkMode,
                            'bg-dark text-light placeholder-light': isDarkMode
                          }"
                        ></b-form-input>
                        <span class="email-domain">@rpsg.in</span>
                        <b-button :variant="isDarkMode ? 'primary' : 'success'" @click="addEmail" size="sm">
                          <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" /> ADD EMAIL
                        </b-button>
                      </div>

                      <div class="email-tags">
                        <span
                          v-for="(email, index) in form.emailTags"
                          :key="index"
                          class="email-tag"
                          :class="{ 'bg-dark text-light': isDarkMode }"
                        >
                          {{ email }}@rpsg.in
                          <button
                            type="button"
                            @click="removeEmailTag(index)"
                            class="email-tag-remove"
                            :class="{ 'text-light': isDarkMode }"
                          >
                            ×
                          </button>
                        </span>
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Dynamic Notes Section -->
                <b-row>
                  <b-col v-for="(note, noteIndex) in form.notes" :key="noteIndex" md="6">
                    <b-form-group>
                      <div class="d-flex align-items-center mb-2">
                        <label>{{ `Note ${noteIndex + 1}` }}</label>
                        <span
                          title="Add Attachment"
                          class="ml-3"
                          style="cursor: pointer"
                          @click="addAttachment(noteIndex)"
                        >
                          <font-awesome-icon :icon="['fas', 'paperclip']" class="mr-1" />
                        </span>
                        <button
                          type="button"
                          @click="removeNote(noteIndex)"
                          class="btn btn-sm ml-auto"
                          :class="isDarkMode ? 'btn-outline-light' : 'btn-outline-danger'"
                          v-if="form.notes.length > 1"
                        >
                          <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" /> Remove
                        </button>
                      </div>

                      <b-form-textarea
                        v-model="note.content"
                        :placeholder="`Enter detailed notes for Note ${noteIndex + 1}`"
                        rows="5"
                        required
                        :class="{
                          'bg-light text-dark': !isDarkMode,
                          'bg-dark text-light placeholder-light': isDarkMode
                        }"
                      ></b-form-textarea>

                      <div class="mt-2">
                        <!-- Show attachments if any -->
                        <div v-if="note.attachments && note.attachments.length > 0" class="mt-2">
                          <div
                            v-for="(attachment, attIndex) in note.attachments"
                            :key="attIndex"
                            class="attachment-item"
                          >
                            <!-- Show image if attachment is an image -->
                            <div v-if="isImageFile(attachment)" class="attachment-image-container">
                              <img
                                :src="getImagePreview(attachment)"
                                :alt="attachment.name"
                                class="attachment-image clickable-image"
                                @error="handleImageError"
                                @click="openImageModal(attachment)"
                              />
                              <div class="attachment-overlay">
                                <span class="attachment-name">{{ attachment.name }}</span>
                                <button
                                  type="button"
                                  @click="removeAttachment(noteIndex, attIndex)"
                                  class="btn btn-sm"
                                  :class="isDarkMode ? 'btn-outline-light' : 'btn-outline-danger'"
                                >
                                  <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                </button>
                              </div>
                            </div>

                            <!-- Show chip for non-image files -->
                            <div v-else class="attachment-chip" :class="{ 'bg-dark text-light': isDarkMode }">
                              <font-awesome-icon :icon="['fas', `${getFileIcon(attachment)}`]" class="mr-1" />
                              <span class="attachment-name">{{ attachment.name }}</span>
                              <button
                                type="button"
                                @click="removeAttachment(noteIndex, attIndex)"
                                class="btn btn-sm ml-2"
                                :class="isDarkMode ? 'btn-outline-light' : 'btn-outline-danger'"
                              >
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="12">
                    <button
                      type="button"
                      @click="addMoreNotes"
                      class="btn add-more-btn"
                      :class="isDarkMode ? 'btn-outline-light' : 'btn-outline-primary'"
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" /> ADD MORE
                    </button>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="12" class="text-right mt-4">
                    <b-button
                      type="submit"
                      :variant="isDarkMode ? 'primary' : 'success'"
                      :disabled="loading"
                      class="mr-2"
                    >
                      SUBMIT
                    </b-button>
                    <b-button type="button" :variant="isDarkMode ? 'outline-light' : 'outline-primary'" @click="onSave">
                      SAVE
                    </b-button>
                  </b-col>
                </b-row>
              </b-form>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </div>

    <!-- Image Preview Modal -->
    <b-modal
      v-model="showImageModal"
      size="lg"
      centered
      hide-footer
      title="Image Preview"
      @hide="closeImageModal"
      :body-class="isDarkMode ? 'bg-dark text-light' : ''"
      :header-class="isDarkMode ? 'bg-dark text-light' : ''"
    >
      <div v-if="selectedImage" class="image-modal-content">
        <img
          :src="getImagePreview(selectedImage)"
          :alt="selectedImage.name"
          class="modal-image"
          @error="handleImageError"
        />
        <div class="image-info mt-3" :class="{ 'bg-dark text-light': isDarkMode }">
          <h6>{{ selectedImage.name }}</h6>
          <small :class="isDarkMode ? 'text-light' : 'text-muted'">
            Size: {{ formatFileSize(selectedImage.size) }} | Type: {{ selectedImage.type }}
          </small>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import router from '@/router';
import moment from 'moment';

export default {
  name: 'StoreVisitNotesForm',
  data() {
    return {
      form: {
        subject: '',
        storeid: '',
        role: '',
        tags: [],
        sendEmail: false,
        notes: [
          {
            content: '',
            attachments: []
          }
        ],
        emailTags: []
      },
      newEmail: '',
      showImageModal: false,
      selectedImage: null
    };
  },
  computed: {
    ...mapState('notes', ['loading', 'storeVisitNote']),
    ...mapState('store', ['storeList']),
    ...mapState('darkMode', ['isDarkMode']),
    isEdit() {
      return this.$route.params.id;
    },
    title() {
      return this.isEdit ? 'Edit Store Visit Note' : 'Create New Note';
    }
  },
  async mounted() {
    // Load store list
    this.listStore({ router });

    if (this.isEdit) {
      await this.loadStoreVisitNote();
    }
  },
  methods: {
    ...mapActions('notes', ['getOne', 'createStoreVisitNote', 'updateStoreVisitNote']),
    ...mapActions('store', ['listStore']),
    async loadStoreVisitNote() {
      try {
        await this.getOne({
          query: { id: this.$route.params.id }
        });
        // Populate form with existing data
        if (this.storeVisitNote) {
          this.form = {
            subject: this.storeVisitNote.subject || '',
            storeid: this.storeVisitNote.storeid || '',
            role: this.storeVisitNote.tags && this.storeVisitNote.tags.includes('HO') ? 'HO' : '',
            tags: this.storeVisitNote.tags || [],
            sendEmail: this.storeVisitNote.sendEmailTo && this.storeVisitNote.sendEmailTo.length > 0,
            notes: this.storeVisitNote.content
              ? this.storeVisitNote.content.map(item => ({
                  content: item.text || '',
                  attachments: item.image
                    ? [
                        {
                          name: 'image.jpg',
                          url: item.image,
                          type: 'image/jpeg'
                        }
                      ]
                    : []
                }))
              : [{ content: '', attachments: [] }],
            emailTags: this.storeVisitNote.sendEmailTo || []
          };
        }
      } catch (error) {
        this.$bvToast.toast('Error loading note data. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    onStoreChange() {},
    onRoleChange() {
      // Reset email fields when role changes
      if (this.form.role !== 'HO') {
        this.form.sendEmail = false;
        this.form.emailTags = [];
      }
    },
    addEmail() {
      if (this.newEmail.trim() && !this.form.emailTags.includes(this.newEmail.trim())) {
        this.form.emailTags.push(this.newEmail.trim());
        this.newEmail = '';
      }
    },
    removeEmail() {
      this.newEmail = '';
    },
    removeEmailTag(index) {
      this.form.emailTags.splice(index, 1);
    },
    addMoreNotes() {
      this.form.notes.push({
        content: '',
        attachments: []
      });
    },
    removeNote(noteIndex) {
      if (this.form.notes.length > 1) {
        this.form.notes.splice(noteIndex, 1);
      }
    },
    addAttachment(noteIndex) {
      // Create file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      input.accept = 'image/*,.pdf,.doc,.docx';
      input.onchange = event => {
        const files = Array.from(event.target.files);
        files.forEach(file => {
          if (!this.form.notes[noteIndex].attachments) {
            this.form.notes[noteIndex].attachments = [];
          }
          this.form.notes[noteIndex].attachments.push({
            name: file.name,
            file: file,
            size: file.size,
            type: file.type
          });
        });
      };
      input.click();
    },
    removeAttachment(noteIndex, attachmentIndex) {
      this.form.notes[noteIndex].attachments.splice(attachmentIndex, 1);
    },
    isImageFile(attachment) {
      if (!attachment.type) return false;
      return attachment.type.startsWith('image/');
    },
    getImagePreview(attachment) {
      if (attachment.file) {
        return URL.createObjectURL(attachment.file);
      }
      return attachment.url || '';
    },
    getFileIcon(attachment) {
      if (!attachment.type) return 'fa-file';

      const type = attachment.type.toLowerCase();
      if (type.includes('pdf')) return 'fa-file-pdf text-danger';
      if (type.includes('word') || type.includes('document')) return 'fa-file-word text-primary';
      if (type.includes('excel') || type.includes('spreadsheet')) return 'fa-file-excel text-success';
      if (type.includes('powerpoint') || type.includes('presentation')) return 'fa-file-powerpoint text-warning';
      if (type.includes('text')) return 'fa-file-alt text-info';
      if (type.includes('zip') || type.includes('rar') || type.includes('archive'))
        return 'fa-file-archive text-secondary';

      return 'fa-file text-muted';
    },
    handleImageError(event) {
      // If image fails to load, show as chip instead
      event.target.style.display = 'none';
      event.target.parentElement.classList.add('image-load-error');
    },
    async onSubmit() {
      try {
        // Process images and convert to base64
        const processedNotes = await Promise.all(
          this.form.notes.map(async note => {
            let imageBase64 = null;

            if (note.attachments && note.attachments.length > 0) {
              // Find the first image attachment
              const imageAttachment = note.attachments.find(
                att => att.file && att.type && att.type.startsWith('image/')
              );

              if (imageAttachment) {
                imageBase64 = await this.convertFileToBase64(imageAttachment.file);
              }
            }

            return {
              text: note.content,
              image: imageBase64
            };
          })
        );

        // Append @rspg.in to each email before sending
        const emailsWithDomain = this.form.emailTags.map(email => (email.includes('@') ? email : `${email}@rspg.in`));

        const formData = {
          subject: this.form.subject,
          storeid: this.form.storeid,
          tags: this.form.role === 'HO' ? ['HO'] : [],
          emails: emailsWithDomain,
          notes: processedNotes
        };

        if (this.isEdit) {
          await this.updateStoreVisitNote({
            id: this.$route.params.id,
            data: formData,
            router
          });
        } else {
          await this.createStoreVisitNote({
            data: formData,
            router
          });
        }
      } catch (error) {
        this.$bvToast.toast('Error processing form data. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    async onSave() {
      try {
        // Process images and convert to base64
        const processedNotes = await Promise.all(
          this.form.notes.map(async note => {
            let imageBase64 = null;

            if (note.attachments && note.attachments.length > 0) {
              // Find the first image attachment
              const imageAttachment = note.attachments.find(
                att => att.file && att.type && att.type.startsWith('image/')
              );

              if (imageAttachment) {
                imageBase64 = await this.convertFileToBase64(imageAttachment.file);
              }
            }

            return {
              text: note.content,
              image: imageBase64
            };
          })
        );

        // Append @rspg.in to each email before sending
        const emailsWithDomain = this.form.emailTags.map(email => (email.includes('@') ? email : `${email}@rspg.in`));

        const formData = {
          subject: this.form.subject,
          storeid: this.form.storeid,
          tags: this.form.role === 'HO' ? ['HO'] : [],
          emails: emailsWithDomain,
          notes: processedNotes
        };

        // For draft, we'll use the same API but with isSubmitted: false
        // We need to modify the store action to handle draft saves
        if (this.isEdit) {
          await this.updateStoreVisitNote({
            id: this.$route.params.id,
            data: { ...formData, isDraft: true },
            router
          });
        } else {
          await this.createStoreVisitNote({
            data: { ...formData, isDraft: true },
            router
          });
        }
      } catch (error) {
        this.$bvToast.toast('Error saving draft. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    onCancel() {
      router.push('/notes');
    },
    openImageModal(attachment) {
      this.selectedImage = attachment;
      this.showImageModal = true;
    },
    closeImageModal() {
      this.showImageModal = false;
      this.selectedImage = null;
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    convertFileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    }
  },
  watch: {
    storeList(newValue) {
      // Store list is already in the correct format from the store module
      // No additional processing needed as it's handled by the store module
    }
  }
};
</script>

<style scoped>
.page-title {
  margin-bottom: 2rem;
}

.add-more-btn {
  border: 2px dashed #007bff;
  background: transparent;
  color: #007bff;
  padding: 10px 20px;
  border-radius: 5px;
  font-weight: bold;
}

.email-modal-content {
  padding: 20px 0;
}

.email-input-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.email-input {
  flex: 1;
}

.email-domain {
  color: #666;
  font-weight: bold;
}

.email-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.email-tag {
  background-color: #e9ecef;
  color: #495057;
  padding: 4px 8px;
  border-radius: 15px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.email-tag-remove {
  background: none;
  border: none;
  color: #495057;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-left: 5px;
}

.email-field-row {
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 10px;
  background-color: #f8f9fa;
}

.email-input-field {
  flex: 1;
}

.email-domain {
  color: #666;
  font-weight: bold;
  white-space: nowrap;
}

.attachment-item {
  display: inline-block;
  margin: 5px;
  font-size: 12px;
}

.attachment-image-container {
  position: relative;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 200px;
  max-height: 150px;
}

.attachment-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
}

.attachment-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.attachment-image-container:hover .attachment-overlay {
  opacity: 1;
}

.attachment-chip {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  padding: 8px 12px;
  border-radius: 20px;
  margin: 2px;
  font-size: 12px;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
}

.attachment-chip:hover {
  background-color: #d1ecf1;
  border-color: #bee5eb;
}

.attachment-chip i {
  margin-right: 6px;
  font-size: 14px;
}

.attachment-name {
  margin-right: 8px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-load-error {
  display: none;
}

.image-load-error + .attachment-chip {
  display: inline-flex;
}

/* Image Modal Styles */
.clickable-image {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.clickable-image:hover {
  transform: scale(1.05);
}

.image-modal-content {
  text-align: center;
}

.modal-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.image-info {
  text-align: left;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 5px;
  border: 1px solid #dee2e6;
}

.image-info h6 {
  margin-bottom: 5px;
  color: #495057;
}

.image-info small {
  font-size: 0.875rem;
}

/* Dark mode specific styles */
.bg-dark .email-domain {
  color: #adb5bd;
}

.bg-dark .attachment-chip {
  background-color: #495057;
  border-color: #6c757d;
}

.bg-dark .attachment-chip:hover {
  background-color: #6c757d;
  border-color: #adb5bd;
}

.bg-dark .email-tag {
  background-color: #495057;
  color: #f8f9fa;
}

.bg-dark .email-tag-remove {
  color: #f8f9fa;
}

.bg-dark .email-tag-remove:hover {
  color: #dc3545;
}
</style>
